import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { SceneObject } from '../../core/SceneObject';
import { MorphStretch } from '../../behaviors/morphstretch';
import { Layout } from '../../behaviors/layout';
import { AddBehaviorCommand } from '../undo/AddBehaviorCommand';
import { BehaviorCommitCommand } from '../undo/BehaviorCommitCommand';
import { applySnapshot, TransformCommand } from '../undo/TransformCommand';
import type { UndoCommand } from '../undo/UndoCommand';
import { Logger } from '../../utils/Logger';

/** The cube that contains owned geometry in its own frame; layout scale can be handed into the box. */
export function solidBoxEdge(object: SceneObject, { withScale = false } = {}): number | null {
    const meshes = object.getOwnedMeshes().filter(mesh => !['__selection', '__collider'].some(suffix => mesh.name.endsWith(suffix)));
    if (!meshes.length) return null;
    object.transform.computeWorldMatrix(true);
    const toRoot = Matrix.Invert(object.transform.getWorldMatrix());
    const min = new Vector3(Infinity, Infinity, Infinity);
    const max = new Vector3(-Infinity, -Infinity, -Infinity);
    const corner = new Vector3();
    for (const mesh of meshes) {
        const relative = mesh.computeWorldMatrix(true).multiply(toRoot);
        for (const vertex of mesh.getBoundingInfo().boundingBox.vectors) {
            Vector3.TransformCoordinatesToRef(vertex, relative, corner);
            min.minimizeInPlace(corner);
            max.maximizeInPlace(corner);
        }
    }
    const size = max.subtract(min);
    if (withScale) {
        const { x, y, z } = object.transform.scaling;
        size.multiplyInPlace(new Vector3(Math.abs(x), Math.abs(y), Math.abs(z)));
    }
    const edge = Math.max(size.x, size.y, size.z);
    return Number.isFinite(edge) && edge > 0 ? edge : null;
}
export function solidBoxData(edge: number): { fit: 'contain'; width: number; height: number; depth: number } {
    return { fit: 'contain', width: edge, height: edge, depth: edge };
}

/** Existing sized rigs keep their own contract. Unsized/free doodads get one fixed contain box. */
export async function boxDoodad(object: SceneObject): Promise<UndoCommand[]> {
    try {
        const stretch = object.getBehavior(MorphStretch);
        if (stretch) {
            const { width, height, depth } = stretch.getData();
            if (width !== undefined || height !== undefined || depth !== undefined) return [];
        }
        const layoutOwned = !!object.getParent()?.getBehavior(Layout);
        const edge = solidBoxEdge(object, { withScale: layoutOwned });
        if (edge === null) return [];
        let box: UndoCommand;
        if (stretch) {
            const before = stretch.getData();
            const errors = await stretch.commitSnapshot({ ...before, ...solidBoxData(edge) });
            if (errors.length) { Logger.warn('[forms] Size box refused:', errors); return []; }
            box = new BehaviorCommitCommand(object, MorphStretch.behaviorType, before, stretch.getData());
        } else {
            const behavior = await object.addBehavior(MorphStretch, solidBoxData(edge));
            box = new AddBehaviorCommand(object, MorphStretch.behaviorType, behavior.getData());
        }
        if (!layoutOwned || object.transform.scaling.equalsToFloats(1, 1, 1)) return [box];
        const before = TransformCommand.capture(object);
        // The engine's existing snapshot writer folds the pose into Base past a cached Properties baseline.
        applySnapshot(object, { ...before, scale: [1, 1, 1] });
        return [box, new TransformCommand(object, before, TransformCommand.capture(object))];
    } catch (error) {
        Logger.warn('[forms] Could not add a doodad box; swapping without it:', error);
        return [];
    }
}
